import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { iznos, kolicina } from '../lib/format'
import type { Nalog } from '../lib/tipovi'

/** Drugi korak prijema (DE-8): računar unosi cijene sa fakture i knjiži.
 *
 *  Magacioner je već prebrojao i predao; količine su od tog trenutka zaključane na serveru
 *  (BB038), pa se ovdje uređuju samo cijene. Kolone količine su namjerno samo za čitanje —
 *  ne zato što bi ih aplikacija odbila, nego zato što bi ih odbila baza, pa je poštenije
 *  ne ponuditi polje nego dati grešku poslije kucanja.
 *
 *  Knjiženje bez cijena je zabranjeno ovdje, a ne na serveru: `proknjizi_dokument` prijem
 *  bez `fakturna_vrijednost` prima i upiše `vrijednost = 0.00` bez ijednog upozorenja, što
 *  vuče prosječnu nabavnu cijenu (C6) ka nuli. Dok se to ne zatvori u bazi, ovaj ekran je
 *  jedina brana — zato dugme stoji onemogućeno sa vidljivim razlogom (DR-2A).
 */

type Red = {
  id: string
  sifra: string
  naziv: string
  jedinica: string
  kolicinaOsn: number
  fakturna: string
  rabat: string
  ztn: string
}

const ZAREZ = (s: string) => s.replace(',', '.')
const broj = (s: string) => (s.trim() === '' ? null : Number(ZAREZ(s)))

export function PrijemCijene({
  dokumentId,
  partnerNaziv,
  nalog,
  naIzlaz,
}: {
  dokumentId: string
  partnerNaziv: string | null
  nalog: Nalog | null
  naIzlaz: (poruka: string | null) => void
}) {
  const [redovi, postaviRedove] = useState<Red[]>([])
  const [brojRacuna, postaviBrojRacuna] = useState('')
  const [datumRacuna, postaviDatumRacuna] = useState('')
  const [ucitava, postaviUcitava] = useState(true)
  const [radi, postaviRadi] = useState(false)
  const [greska, postaviGresku] = useState<string | null>(null)

  const jeAdmin = nalog?.uloga === 'admin'

  useEffect(() => {
    let otkazano = false
    async function ucitaj() {
      const [odgStavke, odgDokument] = await Promise.all([
        supabase
          .from('stavke_dokumenta')
          .select(
            'id, kolicina_osn, fakturna_vrijednost, rabat_iznos, ztn_iznos, sifra_snapshot, naziv_snapshot, jedinica_snapshot',
          )
          .eq('dokument_id', dokumentId)
          .order('redni_broj'),
        supabase
          .from('dokumenti')
          .select('broj_racuna_dobavljaca, datum_racuna_dobavljaca')
          .eq('id', dokumentId)
          .single(),
      ])
      if (otkazano) return

      if (odgStavke.error) {
        postaviGresku(`Stavke se ne mogu učitati: ${odgStavke.error.message}`)
        postaviUcitava(false)
        return
      }

      postaviRedove(
        (odgStavke.data ?? []).map((r) => {
          const s = r as unknown as {
            id: string
            kolicina_osn: string
            fakturna_vrijednost: string | number | null
            rabat_iznos: string | number | null
            ztn_iznos: string | number | null
            sifra_snapshot: string | null
            naziv_snapshot: string | null
            jedinica_snapshot: string | null
          }
          return {
            id: s.id,
            sifra: s.sifra_snapshot ?? '—',
            naziv: s.naziv_snapshot ?? '—',
            jedinica: s.jedinica_snapshot ?? '',
            kolicinaOsn: Number(s.kolicina_osn),
            fakturna: s.fakturna_vrijednost === null ? '' : String(s.fakturna_vrijednost),
            rabat: s.rabat_iznos === null ? '' : String(s.rabat_iznos),
            ztn: s.ztn_iznos === null ? '' : String(s.ztn_iznos),
          }
        }),
      )

      const d = odgDokument.data as { broj_racuna_dobavljaca: string | null; datum_racuna_dobavljaca: string | null } | null
      if (d) {
        postaviBrojRacuna(d.broj_racuna_dobavljaca ?? '')
        postaviDatumRacuna(d.datum_racuna_dobavljaca ?? '')
      }
      postaviUcitava(false)
    }
    void ucitaj()
    return () => {
      otkazano = true
    }
  }, [dokumentId])

  function izmijeni(id: string, polje: 'fakturna' | 'rabat' | 'ztn', vrijednost: string) {
    postaviRedove((prethodni) =>
      prethodni.map((r) => (r.id === id ? { ...r, [polje]: vrijednost } : r)),
    )
  }

  const bezCijene = useMemo(
    () => redovi.filter((r) => broj(r.fakturna) === null || Number.isNaN(broj(r.fakturna))),
    [redovi],
  )

  const ukupno = useMemo(
    () =>
      redovi.reduce(
        (z, r) => z + (broj(r.fakturna) ?? 0) - (broj(r.rabat) ?? 0) + (broj(r.ztn) ?? 0),
        0,
      ),
    [redovi],
  )

  async function sacuvaj(): Promise<boolean> {
    postaviGresku(null)
    for (const r of redovi) {
      const f = broj(r.fakturna)
      if (f !== null && Number.isNaN(f)) {
        postaviGresku(`Neispravan iznos kod ${r.sifra}.`)
        return false
      }
      const { error } = await supabase
        .from('stavke_dokumenta')
        .update({
          fakturna_vrijednost: f,
          rabat_iznos: broj(r.rabat) ?? 0,
          ztn_iznos: broj(r.ztn) ?? 0,
        })
        .eq('id', r.id)
      if (error) {
        postaviGresku(`Cijena za ${r.sifra} nije sačuvana: ${error.message}`)
        return false
      }
    }

    const { error: greskaDokumenta } = await supabase
      .from('dokumenti')
      .update({
        broj_racuna_dobavljaca: brojRacuna.trim() === '' ? null : brojRacuna.trim(),
        datum_racuna_dobavljaca: datumRacuna === '' ? null : datumRacuna,
      })
      .eq('id', dokumentId)

    if (greskaDokumenta) {
      postaviGresku(`Podaci o računu nisu sačuvani: ${greskaDokumenta.message}`)
      return false
    }
    return true
  }

  async function knjizi() {
    postaviRadi(true)
    const sacuvano = await sacuvaj()
    if (!sacuvano) {
      postaviRadi(false)
      return
    }

    const { data, error } = await supabase.rpc('proknjizi_dokument', { p_dokument: dokumentId })
    postaviRadi(false)

    if (error) {
      postaviGresku(`Knjiženje nije prošlo (${error.code ?? 'bez koda'}): ${error.message}`)
      return
    }
    const ishod = data as { broj?: string } | null
    naIzlaz(`Prijem je proknjižen pod brojem ${ishod?.broj ?? '—'}.`)
  }

  async function vrati() {
    postaviRadi(true)
    postaviGresku(null)
    const { error } = await supabase.rpc('vrati_na_telefon', { p_dokument: dokumentId })
    postaviRadi(false)
    if (error) {
      postaviGresku(`Vraćanje nije prošlo: ${error.message}`)
      return
    }
    naIzlaz('Prijem je vraćen magacioneru na ponovno brojanje.')
  }

  if (ucitava) return <div className="ucitavanje">Učitavanje…</div>

  return (
    <div data-testid="prijem-cijene">
      <div className="traka">
        <div>
          <span className="naslov">{partnerNaziv ?? 'Bez dobavljača'}</span>
          <div className="oznaka">Količine je prebrojao magacioner i više se ne mijenjaju</div>
        </div>
        <button type="button" className="veza-tekst" onClick={() => naIzlaz(null)}>
          ← Nazad
        </button>
      </div>

      {greska && (
        <div className="greska" role="alert" data-testid="cijene-greska">
          {greska}
        </div>
      )}

      <div className="obrazac" style={{ marginBottom: 'var(--desk-4)' }}>
        <div className="obrazac__polje">
          <label className="oznaka polje-oznaka" htmlFor="polje-racun">
            Broj računa dobavljača
          </label>
          <input
            id="polje-racun"
            className="polje"
            value={brojRacuna}
            onChange={(e) => postaviBrojRacuna(e.target.value)}
            data-testid="polje-racun"
          />
        </div>
        <div className="obrazac__polje">
          <label className="oznaka polje-oznaka" htmlFor="polje-datum-racuna">
            Datum računa
          </label>
          <input
            id="polje-datum-racuna"
            className="polje"
            type="date"
            value={datumRacuna}
            onChange={(e) => postaviDatumRacuna(e.target.value)}
            data-testid="polje-datum-racuna"
          />
        </div>
      </div>

      <div className="tabela-okvir">
        <table className="tabela" data-testid="cijene-tabela">
          <thead>
            <tr>
              <th scope="col">Šifra</th>
              <th scope="col">Naziv</th>
              <th scope="col" className="desno">Količina</th>
              <th scope="col" className="desno">Fakturna vrijednost</th>
              <th scope="col" className="desno">Rabat</th>
              <th scope="col" className="desno">ZTN</th>
              <th scope="col" className="desno">Vrijednost</th>
            </tr>
          </thead>
          <tbody>
            {redovi.map((r) => {
              const vrijednost = (broj(r.fakturna) ?? 0) - (broj(r.rabat) ?? 0) + (broj(r.ztn) ?? 0)
              return (
                <tr key={r.id} data-testid="cijene-red" data-sifra={r.sifra}>
                  <td className="tabela__sifra">{r.sifra}</td>
                  <td className="tabela__naziv">{r.naziv}</td>
                  {/* Količina je zaključana na serveru poslije predaje (BB038), pa ovdje
                      stoji kao tekst — polje koje baza svakako odbija je zamka, ne ponuda. */}
                  <td className="tabela__broj desno">
                    {kolicina(r.kolicinaOsn)} {r.jedinica}
                  </td>
                  <td className="desno">
                    <input
                      className="polje polje--iznos"
                      inputMode="decimal"
                      value={r.fakturna}
                      onChange={(e) => izmijeni(r.id, 'fakturna', e.target.value)}
                      aria-label={`Fakturna vrijednost za ${r.sifra}`}
                      data-testid="polje-fakturna"
                    />
                  </td>
                  <td className="desno">
                    <input
                      className="polje polje--iznos"
                      inputMode="decimal"
                      value={r.rabat}
                      onChange={(e) => izmijeni(r.id, 'rabat', e.target.value)}
                      aria-label={`Rabat za ${r.sifra}`}
                    />
                  </td>
                  <td className="desno">
                    <input
                      className="polje polje--iznos"
                      inputMode="decimal"
                      value={r.ztn}
                      onChange={(e) => izmijeni(r.id, 'ztn', e.target.value)}
                      aria-label={`ZTN za ${r.sifra}`}
                    />
                  </td>
                  <td className="tabela__broj desno">{iznos(vrijednost)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="traka" style={{ marginTop: 'var(--desk-4)', borderBottom: 0 }}>
        <span className="oznaka">
          Ukupno: <span className="broj">{iznos(ukupno)}</span> EUR
        </span>
        <div className="traka__desno">
          {jeAdmin && (
            <button
              type="button"
              className="dugme dugme--sporedno"
              disabled={radi}
              onClick={() => void vrati()}
              data-testid="cijene-vrati"
            >
              Vrati na telefon
            </button>
          )}
          <button
            type="button"
            className="dugme dugme--glavno"
            disabled={radi || bezCijene.length > 0 || redovi.length === 0}
            onClick={() => void knjizi()}
            data-testid="cijene-knjizi"
          >
            {radi ? 'Knjiženje…' : 'Proknjiži'}
          </button>
        </div>
      </div>

      {bezCijene.length > 0 && (
        <div className="razlog" data-testid="cijene-razlog">
          Bez cijene: {bezCijene.map((r) => r.sifra).join(', ')}. Prijem proknjižen bez cijena
          upisao bi vrijednost nula i pokvario prosječnu nabavnu cijenu (C6).
        </div>
      )}
    </div>
  )
}
